import type { MouseEvent } from "react";
import { useCallback, useId } from "react";
import type { ITagProps, ITagSize } from "./types";
import "./index.css";
import { clsx } from "clsx";
import { X } from "lucide-react";
import { useTranslations } from "../../i18n";
import { Text } from "../Text";
import type { ITextSize } from "../Text/types";

const textSize: Record<ITagSize, ITextSize> = {
  sm: "sm",
  md: "md",
  lg: "md",
  xl: "md",
};

export const Tag = ({
  color = "neutral",
  size = "sm",
  shape = "rounded",
  text,
  startIcon,
  onRemove,
  onClick,
  disabled,
  className,
  ref,
  ...htmlProps
}: ITagProps) => {
  const { t } = useTranslations();
  const textId = useId();
  const removeId = useId();

  const rootClass: string = clsx(
    "nuco-tag",
    `nuco-color-${color}`,
    `nuco-tag--${size}`,
    `nuco-tag--${shape}`,
    !disabled && onClick && "nuco-tag--clickable",
    className,
  );

  const handleClick = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation();
      if (disabled) {
        return;
      }
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled],
  );

  const handleRemove = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation();
      if (disabled) {
        return;
      }
      if (onRemove) {
        onRemove(e);
      }
    },
    [onRemove, disabled],
  );

  return (
    <div ref={ref} className={rootClass} onClick={handleClick} {...htmlProps}>
      {startIcon && <div className="nuco-tag__icon">{startIcon}</div>}
      <Text
        as="div"
        size={textSize[size]}
        id={textId}
        className="nuco-tag__text"
      >
        {text}
      </Text>
      {!disabled && onRemove && (
        // Named "Remove <tag text>": the button's own label, then the text.
        <button
          type="button"
          id={removeId}
          className="nuco-tag__remove"
          aria-label={t("ui.tag.remove")}
          aria-labelledby={`${removeId} ${textId}`}
          onClick={handleRemove}
        >
          <X aria-hidden />
        </button>
      )}
    </div>
  );
};
